import { useRef, useEffect, useState, useCallback } from 'react';
import {
  loadSpeechSettings,
  saveSpeechSettings,
  isSpeechSynthesisSupported,
  stripHtmlTags,
} from '../utils/captionSpeechSettings';
import { showSpeechSettingsDialog } from '../utils/speechSettingsDialog';
import type { CaptionSpeechSettings } from '../utils/captionSpeechSettings';

/**
 * Hook that manages reading captions aloud using the Web Speech Synthesis API.
 *
 * New cues are **queued** — the current utterance is never cancelled when a new
 * cue arrives.  The queue is only flushed on explicit user actions (pause, seek,
 * unmount).
 */
export function useCaptionSpeech(
  activeCueText: string,
  activeCueEnd: number,
  isPlaying: boolean,
  isSeeking: boolean,
  currentTime: number,
  mediaEl: HTMLMediaElement | null,
): {
  speechEnabled: boolean;
  setSpeechEnabled: (enabled: boolean) => void;
  showSettingsDialog: () => void;
} {
  const [speechEnabled, setSpeechEnabledState] = useState(() => {
    if (!isSpeechSynthesisSupported()) return false;
    return loadSpeechSettings().enabled;
  });

  const settingsRef = useRef<CaptionSpeechSettings>(loadSpeechSettings());
  const savedVolumeRef = useRef<number | null>(null);
  const speechPausedVideoRef = useRef(false);
  const overrunTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSpokenTextRef = useRef<string>('');
  const isPlayingRef = useRef(isPlaying);
  const currentTimeRef = useRef(currentTime);
  const mediaElRef = useRef(mediaEl);

  // Keep refs in sync
  isPlayingRef.current = isPlaying;
  currentTimeRef.current = currentTime;
  mediaElRef.current = mediaEl;

  const restorePlaybackState = useCallback(() => {
    const el = mediaElRef.current;
    if (!el) return;

    // Clear overrun timer first
    if (overrunTimerRef.current !== null) {
      clearTimeout(overrunTimerRef.current);
      overrunTimerRef.current = null;
    }

    // Restore volume if ducked
    if (savedVolumeRef.current !== null) {
      el.volume = savedVolumeRef.current;
      savedVolumeRef.current = null;
    }

    // Resume video if we paused it. Keep speechPausedVideoRef true
    // until el.play() succeeds — otherwise the "cancel on pause"
    // effect sees isPlaying=false + speechPausedVideoRef=false and
    // calls cancelSpeech(), clearing lastSpokenTextRef. When playback
    // then resumes, the same caption is re-spoken, causing an infinite
    // pause loop (especially in Safari where event timing differs).
    if (speechPausedVideoRef.current) {
      el.play().then(
        () => { speechPausedVideoRef.current = false; },
        () => { speechPausedVideoRef.current = false; },
      );
    }
  }, []);

  const cancelSpeech = useCallback(() => {
    if (!isSpeechSynthesisSupported()) return;
    speechSynthesis.cancel();
    lastSpokenTextRef.current = '';
    restorePlaybackState();
  }, [restorePlaybackState]);

  const speakText = useCallback(
    (text: string, cueEnd: number) => {
      if (!isSpeechSynthesisSupported()) return;
      const el = mediaElRef.current;
      if (!el) return;

      const plain = stripHtmlTags(text);
      if (!plain) return;

      const settings = settingsRef.current;
      const utterance = new SpeechSynthesisUtterance(plain);

      // Set voice
      if (settings.voiceURI) {
        const voices = speechSynthesis.getVoices();
        const match = voices.find((v) => v.voiceURI === settings.voiceURI);
        if (match) utterance.voice = match;
      }

      utterance.rate = settings.rate;
      utterance.volume = settings.volume;

      // Volume ducking — only duck if not already ducked
      if (settings.playbackBehavior === 'lower-volume' && savedVolumeRef.current === null) {
        savedVolumeRef.current = el.volume;
        el.volume = el.volume * 0.2;
      }

      // Pause behavior
      if (settings.playbackBehavior === 'pause' && !speechPausedVideoRef.current) {
        speechPausedVideoRef.current = true;
        el.pause();
      }

      // Overrun detection — only for non-pause behaviors with pauseOnOverrun enabled
      if (
        settings.playbackBehavior !== 'pause' &&
        settings.pauseOnOverrun &&
        cueEnd > 0
      ) {
        const remainingMs = (cueEnd - currentTimeRef.current) * 1000;
        if (remainingMs > 0) {
          // Clear any previous overrun timer
          if (overrunTimerRef.current !== null) {
            clearTimeout(overrunTimerRef.current);
          }
          overrunTimerRef.current = setTimeout(() => {
            overrunTimerRef.current = null;
            if (speechSynthesis.speaking && !speechPausedVideoRef.current) {
              // The cue has ended but speech is still going. Pause the
              // video but let speech continue — when utterance.onend
              // fires, restorePlaybackState will resume the video.
              speechPausedVideoRef.current = true;
              mediaElRef.current?.pause();
            }
          }, remainingMs);
        }
      }

      // When this utterance ends, check if queue is drained
      utterance.onend = () => {
        if (!speechSynthesis.speaking && !speechSynthesis.pending) {
          restorePlaybackState();
        }
      };

      utterance.onerror = () => {
        if (!speechSynthesis.speaking && !speechSynthesis.pending) {
          restorePlaybackState();
        }
      };

      speechSynthesis.speak(utterance);
    },
    [restorePlaybackState],
  );

  // Queue new cue text when it changes (and speech is enabled + playing).
  // We track the last spoken text to avoid re-speaking the same cue when
  // isPlaying toggles back to true after restorePlaybackState resumes the video.
  useEffect(() => {
    if (!speechEnabled || !isPlaying || !activeCueText) return;
    if (activeCueText === lastSpokenTextRef.current) return;
    lastSpokenTextRef.current = activeCueText;
    speakText(activeCueText, activeCueEnd);
  }, [activeCueText, activeCueEnd, speechEnabled, isPlaying, speakText]);

  // Cancel on pause — only when the *user* paused, not when we paused
  // the video ourselves (for "Pause While Speaking" or overrun).
  useEffect(() => {
    if (!isPlaying && !speechPausedVideoRef.current) {
      cancelSpeech();
    }
  }, [isPlaying, cancelSpeech]);

  // Clear speechPausedVideoRef when user manually resumes playback.
  // This runs on isPlaying transitions from false→true. We use a ref
  // to detect the transition rather than reacting to isPlaying=true
  // (which would fire on mount).
  const prevIsPlayingRef = useRef(isPlaying);
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current;
    prevIsPlayingRef.current = isPlaying;
    if (!wasPlaying && isPlaying && speechPausedVideoRef.current) {
      speechPausedVideoRef.current = false;
    }
  }, [isPlaying]);

  // Cancel on seek
  useEffect(() => {
    if (isSeeking) {
      cancelSpeech();
    }
  }, [isSeeking, cancelSpeech]);

  // Cleanup on unmount
  useEffect(() => {
    return () => cancelSpeech();
  }, [cancelSpeech]);

  const setSpeechEnabled = useCallback(
    (enabled: boolean) => {
      setSpeechEnabledState(enabled);
      saveSpeechSettings({ enabled });
      settingsRef.current = { ...settingsRef.current, enabled };
      if (!enabled) {
        cancelSpeech();
      }
    },
    [cancelSpeech],
  );

  const showSettingsDialogCb = useCallback(() => {
    cancelSpeech();
    showSpeechSettingsDialog((newSettings) => {
      settingsRef.current = newSettings;
      setSpeechEnabledState(newSettings.enabled);
    });
  }, [cancelSpeech]);

  return {
    speechEnabled,
    setSpeechEnabled,
    showSettingsDialog: showSettingsDialogCb,
  };
}
